import { createContext, useContext, useState, useCallback, useMemo, type ReactNode } from 'react'

type PlaceSearchFailureContextValue = {
  permanentlyFailedQueries: ReadonlySet<string>
  firstFailedQueries: ReadonlySet<string>
  markFirstFailed: (searchQuery: string) => void
  markPermanentlyFailed: (searchQuery: string) => void
  resetFailures: () => void
}

const PlaceSearchFailureContext = createContext<PlaceSearchFailureContextValue | null>(null)

export function PlaceSearchFailureProvider({ children }: { children: ReactNode }) {
  const [permanentlyFailedQueries, setPermanentlyFailedQueries] = useState<Set<string>>(new Set())
  const [firstFailedQueries, setFirstFailedQueries] = useState<Set<string>>(new Set())

  const markFirstFailed = useCallback((searchQuery: string) => {
    setFirstFailedQueries(prev => new Set([...prev, searchQuery]))
  }, [])

  const markPermanentlyFailed = useCallback((searchQuery: string) => {
    setPermanentlyFailedQueries(prev => new Set([...prev, searchQuery]))
  }, [])

  const resetFailures = useCallback(() => {
    setPermanentlyFailedQueries(new Set())
    setFirstFailedQueries(new Set())
  }, [])

  const value = useMemo(
    () => ({ permanentlyFailedQueries, firstFailedQueries, markFirstFailed, markPermanentlyFailed, resetFailures }),
    [permanentlyFailedQueries, firstFailedQueries, markFirstFailed, markPermanentlyFailed, resetFailures],
  )

  return (
    <PlaceSearchFailureContext.Provider value={value}>
      {children}
    </PlaceSearchFailureContext.Provider>
  )
}

export function usePlaceSearchFailure() {
  const ctx = useContext(PlaceSearchFailureContext)
  if (!ctx) throw new Error('usePlaceSearchFailure must be within PlaceSearchFailureProvider')
  return ctx
}
